import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetConsignmentLinesQuery, CONSIGNMENT_STATUS, type ConsignmentStatusCode } from '@/entities/consignments';
import { useCurrentProjectData } from '@/entities/layout';
import { usePermissions } from '@/shared/lib';

import { ConsignmentLineRow, SelectedLine, UseConsignmentLinesTableParams } from './types';
import { getConsignmentLineColumns, getConsignmentLineActionColumn } from '../config/columns';

export function useConsignmentLinesTable({
  consignmentId,
  projectId,
  siteId,
  consignmentStatus = CONSIGNMENT_STATUS.NR,
  isReadOnly = false,
  externalData,
  externalIsLoading,
  externalError,
  renderInventoryUnits
}: UseConsignmentLinesTableParams) {
  const { t } = useTranslation();
  const { useConsignmentLinePermissions } = usePermissions();
  const [selectedLine, setSelectedLine] = useState<SelectedLine | null>(null);

  const linePermissions = useConsignmentLinePermissions(isReadOnly, consignmentStatus as ConsignmentStatusCode);
  const canEditLine = linePermissions.canEditLine;
  const canDeleteLine = linePermissions.canDeleteLine;

  // Skip API call if external data is provided
  const {
    data: localData,
    isLoading: localIsLoading,
    error: localError
  } = useGetConsignmentLinesQuery(
    {
      consignmentId,
      projectId,
      siteId
    },
    {
      skip: !consignmentId || !projectId || !siteId || !!externalData
    }
  );

  // Use external data if provided, otherwise use API data
  const data = externalData || localData;

  const project = useCurrentProjectData()?.project;
  const showBatch = project?.isBatchNumberActive ?? true;
  const showExpiry = project?.isExpiryDateActive ?? true;

  const columns = useMemo(
    () => getConsignmentLineColumns(t, data?.totals, renderInventoryUnits, { showBatch, showExpiry }),
    [t, data?.totals, renderInventoryUnits, showBatch, showExpiry]
  );
  const actions = useMemo(
    () =>
      getConsignmentLineActionColumn(
        t,
        (line) => setSelectedLine({ id: line.id, mode: 'view' }),
        canEditLine ? (line) => setSelectedLine({ id: line.id, mode: 'edit' }) : undefined
      ),
    [t, canEditLine]
  );

  const handleCloseModal = useCallback(() => {
    setSelectedLine(null);
  }, []);

  const tableData: ConsignmentLineRow[] = useMemo(() => data?.items.map((item) => ({ ...item })) || [], [data?.items]);

  return {
    // State
    selectedLineId: selectedLine?.id ?? '',
    lineModalMode: selectedLine?.mode ?? 'view',
    isLoading: externalData ? externalIsLoading : localIsLoading,
    error: externalData ? externalError : localError,

    // Data
    tableData,
    columns,
    actions,

    // Handlers
    handleCloseModal,

    // Permissions
    canEditLine,
    canDeleteLine
  };
}
